import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePark } from '../contexts/ParkContext';
import { 
  Settings,
  Plus, 
  Trash2, 
  Edit2,
  FolderTree,
  ChevronRight,
  Save,
  X,
  Building2,
  MapPin,
  Download,
  Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Configuraciones = () => {
  const { isMaster } = useAuth();
  const { refreshParks } = usePark();
  const fileInputRef = useRef(null);
  
  // Categories state
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ nombre: '', descripcion: '' });
  const [subcategoryForm, setSubcategoryForm] = useState({ nombre: '', descripcion: '' });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', id: null, name: '' });

  // Parks state (for Master users)
  const [myParks, setMyParks] = useState([]);
  const [loadingParks, setLoadingParks] = useState(true);
  const [showParkModal, setShowParkModal] = useState(false);
  const [editingPark, setEditingPark] = useState(null);
  const [parkForm, setParkForm] = useState({ nombre: '', ubicacion: '', moneda: 'MXN' });

  useEffect(() => {
    fetchCategories();
    if (isMaster()) {
      fetchMyParks();
    }
  }, []);

  // ==================== CATEGORIES ====================
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/config/budget-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Error al cargar las categorías');
    }
    setLoading(false);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API}/config/budget-categories/${editingCategory.id}`, categoryForm);
        toast.success('Categoría actualizada');
      } else {
        await axios.post(`${API}/config/budget-categories`, categoryForm);
        toast.success('Categoría creada');
      }
      setShowCategoryModal(false);
      resetCategoryForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar la categoría');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({ nombre: category.nombre, descripcion: category.descripcion || '' });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await axios.delete(`${API}/config/budget-categories/${categoryId}`);
      toast.success('Categoría eliminada');
      setDeleteConfirm({ open: false, type: '', id: null, name: '' });
      fetchCategories();
    } catch (error) {
      toast.error('Error al eliminar la categoría');
    }
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryForm({ nombre: '', descripcion: '' });
  };

  // Subcategory handlers
  const handleOpenSubcategoryModal = (categoryId) => {
    setSelectedCategoryId(categoryId);
    setShowSubcategoryModal(true);
  };

  const handleSaveSubcategory = async (e) => {
    e.preventDefault();
    try {
      if (editingSubcategory) {
        await axios.put(`${API}/config/budget-subcategories/${editingSubcategory.id}`, subcategoryForm);
        toast.success('Subcategoría actualizada');
      } else {
        await axios.post(`${API}/config/budget-subcategories`, {
          ...subcategoryForm,
          category_id: selectedCategoryId
        });
        toast.success('Subcategoría creada');
      }
      setShowSubcategoryModal(false);
      resetSubcategoryForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar la subcategoría');
    }
  };

  const handleEditSubcategory = (subcategory, categoryId) => {
    setEditingSubcategory(subcategory);
    setSelectedCategoryId(categoryId);
    setSubcategoryForm({ nombre: subcategory.nombre, descripcion: subcategory.descripcion || '' });
    setShowSubcategoryModal(true);
  };

  const handleDeleteSubcategory = async (subcategoryId) => {
    try {
      await axios.delete(`${API}/config/budget-subcategories/${subcategoryId}`);
      toast.success('Subcategoría eliminada');
      setDeleteConfirm({ open: false, type: '', id: null, name: '' });
      fetchCategories();
    } catch (error) {
      toast.error('Error al eliminar la subcategoría');
    }
  };

  const resetSubcategoryForm = () => {
    setEditingSubcategory(null);
    setSelectedCategoryId(null);
    setSubcategoryForm({ nombre: '', descripcion: '' });
  };

  // ==================== CSV TEMPLATE & IMPORT ====================
  const downloadCSVTemplate = () => {
    // Create CSV template with headers and example rows
    const template = `categoria,subcategoria,descripcion_categoria,descripcion_subcategoria
Seguridad,Vigilancia,Gastos relacionados con seguridad,Servicio de vigilancia 24 horas
Seguridad,Cámaras,Gastos relacionados con seguridad,Sistema de videovigilancia
Mantenimiento,Áreas comunes,Gastos de mantenimiento general,Limpieza y conservación
Mantenimiento,Reparaciones,Gastos de mantenimiento general,Reparaciones menores
Servicios,Energía eléctrica,Servicios públicos,Consumo de luz
Servicios,Agua,Servicios públicos,Consumo de agua`;
    
    // Download file
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'plantilla_categorias.csv';
    link.click();
    toast.success('Plantilla descargada');
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/config/budget-categories/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Importación exitosa: ${response.data.created_categories} categorías, ${response.data.created_subcategories} subcategorías`);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar el CSV');
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ==================== PARKS (Master only) ====================
  const fetchMyParks = async () => {
    setLoadingParks(true);
    try {
      const response = await axios.get(`${API}/parks`);
      setMyParks(response.data);
    } catch (error) {
      console.error('Error fetching parks:', error);
      toast.error('Error al cargar los parques');
    }
    setLoadingParks(false);
  };

  const handleSavePark = async (e) => {
    e.preventDefault();
    try {
      if (editingPark) {
        await axios.put(`${API}/parks/${editingPark.id}`, parkForm);
        toast.success('Parque actualizado');
      } else {
        await axios.post(`${API}/parks`, parkForm);
        toast.success('Parque creado exitosamente');
      }
      setShowParkModal(false);
      resetParkForm();
      fetchMyParks();
      // Refresh the global parks context
      if (refreshParks) refreshParks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el parque');
    }
  };

  const handleEditPark = (park) => {
    setEditingPark(park);
    setParkForm({ 
      nombre: park.nombre, 
      ubicacion: park.ubicacion, 
      moneda: park.moneda || 'MXN' 
    });
    setShowParkModal(true);
  };

  const handleDeletePark = async (parkId) => {
    try {
      await axios.delete(`${API}/parks/${parkId}`);
      toast.success('Parque eliminado');
      setDeleteConfirm({ open: false, type: '', id: null, name: '' });
      fetchMyParks();
      if (refreshParks) refreshParks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar el parque');
    }
  };

  const resetParkForm = () => {
    setEditingPark(null);
    setParkForm({ nombre: '', ubicacion: '', moneda: 'MXN' });
  };

  // Determine default tab based on role
  const defaultTab = isMaster() ? 'parques' : 'categorias';

  return (
    <div className="animate-fadeIn">
      <div className="page-header">
        <h1 data-testid="config-title">
          <Settings className="inline-block mr-3 mb-1" size={28} />
          Configuraciones del Sistema
        </h1>
        <p>Personaliza los parámetros del sistema</p>
      </div>

      <Tabs defaultValue={defaultTab} className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-none">
          {isMaster() && (
            <TabsTrigger value="parques" className="rounded-none data-[state=active]:bg-white" data-testid="tab-parques">
              <Building2 size={16} className="mr-2" />
              Mis Parques
            </TabsTrigger>
          )}
          <TabsTrigger value="categorias" className="rounded-none data-[state=active]:bg-white" data-testid="tab-categorias">
            <FolderTree size={16} className="mr-2" />
            Categorías de Presupuesto
          </TabsTrigger>
        </TabsList>

        {/* Parks Tab (Master only) */}
        {isMaster() && (
          <TabsContent value="parques">
            <div className="card-industrial">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold flex items-center gap-2">
                    <Building2 size={20} />
                    Mis Parques Industriales
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Administra los parques industriales que gestionas. Solo podrás asignar usuarios a estos parques.
                  </p>
                </div>
                <Button
                  className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
                  onClick={() => { resetParkForm(); setShowParkModal(true); }}
                  data-testid="add-park-btn"
                >
                  <Plus size={16} className="mr-2" />
                  Nuevo Parque
                </Button>
              </div>

              {loadingParks ? (
                <div className="animate-pulse space-y-4">
                  {[1, 2].map(i => (
                    <div key={i} className="h-24 bg-slate-100 rounded"></div>
                  ))}
                </div>
              ) : myParks.length === 0 ? (
                <div className="text-center py-16 border-2 border-dashed border-slate-200">
                  <Building2 size={48} className="mx-auto mb-4 text-slate-300" />
                  <h3 className="font-bold text-slate-700 mb-2">No tienes parques registrados</h3>
                  <p className="text-slate-500 mb-4">Comienza creando tu primer parque industrial para gestionar</p>
                  <Button
                    className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
                    onClick={() => { resetParkForm(); setShowParkModal(true); }}
                  >
                    <Plus size={16} className="mr-2" />
                    Crear Primer Parque
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {myParks.map((park) => (
                    <div 
                      key={park.id} 
                      className="border border-slate-200 p-5 hover:border-slate-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                            <Building2 size={24} />
                          </div>
                          <div>
                            <h3 className="font-bold text-lg">{park.nombre}</h3>
                            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                              <MapPin size={14} />
                              {park.ubicacion}
                            </p>
                            <p className="text-xs text-slate-400 mt-2">
                              Moneda: {park.moneda || 'MXN'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEditPark(park)}
                            className="p-2 hover:bg-slate-100 transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={16} className="text-slate-500" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({ open: true, type: 'park', id: park.id, name: park.nombre })}
                            className="p-2 hover:bg-red-50 transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 size={16} className="text-red-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {/* Categories Tab */}
        <TabsContent value="categorias">
          <div className="card-industrial">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <FolderTree size={20} />
                  Categorías de Presupuesto
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Define las categorías y subcategorías para organizar el presupuesto y gastos
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImportCSV}
                  accept=".csv"
                  className="hidden"
                />
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={downloadCSVTemplate}
                  data-testid="download-categories-template-btn"
                >
                  <Download size={16} className="mr-2" />
                  Descargar Plantilla
                </Button>
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="import-categories-btn"
                >
                  <Upload size={16} className="mr-2" />
                  Importar CSV
                </Button>
                <Button
                  className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
                  onClick={() => { resetCategoryForm(); setShowCategoryModal(true); }}
                  data-testid="add-category-btn"
                >
                  <Plus size={16} className="mr-2" />
                  Nueva Categoría
                </Button>
              </div>
            </div>

            {/* CSV Format Help */}
            <div className="bg-slate-50 border border-slate-200 p-4 mb-6 text-sm">
              <p className="font-medium text-slate-700 mb-2">Instrucciones para importar categorías:</p>
              <ol className="list-decimal list-inside text-slate-600 space-y-1">
                <li>Descarga la plantilla CSV con el botón "Descargar Plantilla"</li>
                <li>Abre el archivo en Excel o Google Sheets</li>
                <li>Edita o agrega las categorías y subcategorías que necesites</li>
                <li>Guarda el archivo como CSV y usa "Importar CSV" para cargarlo</li>
              </ol>
              <p className="text-slate-500 mt-3">
                <span className="font-medium">Formato:</span>{' '}
                <code className="bg-white px-2 py-0.5 border text-xs">categoria,subcategoria,descripcion_categoria,descripcion_subcategoria</code>
              </p>
            </div>

            {loading ? (
              <div className="animate-pulse space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-slate-100 rounded"></div>
                ))}
              </div>
            ) : categories.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FolderTree size={48} className="mx-auto mb-4 opacity-50" />
                <p>No hay categorías configuradas</p>
                <p className="text-sm">Comienza agregando una categoría de presupuesto o importa un CSV</p>
              </div>
            ) : (
              <Accordion type="multiple" className="space-y-2">
                {categories.map((category) => (
                  <AccordionItem 
                    key={category.id} 
                    value={category.id}
                    className="border border-slate-200 px-4"
                  >
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-slate-900">{category.nombre}</span>
                          <span className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-500">
                            {category.subcategories?.length || 0} subcategorías
                          </span>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pt-2 pb-4">
                        {category.descripcion && (
                          <p className="text-sm text-slate-500 mb-4">{category.descripcion}</p>
                        )}
                        
                        {/* Category Actions */}
                        <div className="flex gap-2 mb-4">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-none"
                            onClick={() => handleEditCategory(category)}
                          >
                            <Edit2 size={14} className="mr-1" />
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-none text-red-600 hover:bg-red-50"
                            onClick={() => setDeleteConfirm({ open: true, type: 'category', id: category.id, name: category.nombre })}
                          >
                            <Trash2 size={14} className="mr-1" />
                            Eliminar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-none ml-auto"
                            onClick={() => handleOpenSubcategoryModal(category.id)}
                          >
                            <Plus size={14} className="mr-1" />
                            Agregar Subcategoría
                          </Button>
                        </div>

                        {/* Subcategories List */}
                        {category.subcategories && category.subcategories.length > 0 ? (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Subcategorías</p>
                            {category.subcategories.map((sub) => (
                              <div 
                                key={sub.id} 
                                className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100"
                              >
                                <div className="flex items-center gap-2">
                                  <ChevronRight size={14} className="text-slate-400" />
                                  <span className="font-medium">{sub.nombre}</span>
                                  {sub.descripcion && (
                                    <span className="text-sm text-slate-400">- {sub.descripcion}</span>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => handleEditSubcategory(sub, category.id)}
                                    className="p-1.5 hover:bg-slate-200 transition-colors rounded"
                                  >
                                    <Edit2 size={14} className="text-slate-500" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirm({ open: true, type: 'subcategory', id: sub.id, name: sub.nombre })}
                                    className="p-1.5 hover:bg-red-100 transition-colors rounded"
                                  >
                                    <Trash2 size={14} className="text-red-500" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-400 italic">Sin subcategorías</p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Park Modal */}
      <Dialog open={showParkModal} onOpenChange={setShowParkModal}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold flex items-center gap-2">
              <Building2 size={20} />
              {editingPark ? 'Editar Parque' : 'Nuevo Parque Industrial'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSavePark}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="label-overline">Nombre del Parque</Label>
                <Input
                  value={parkForm.nombre}
                  onChange={(e) => setParkForm({ ...parkForm, nombre: e.target.value })}
                  placeholder="Ej: Parque Industrial del Norte"
                  required
                  className="input-industrial"
                  data-testid="park-nombre-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Ubicación</Label>
                <Input
                  value={parkForm.ubicacion}
                  onChange={(e) => setParkForm({ ...parkForm, ubicacion: e.target.value })}
                  placeholder="Ej: Monterrey, Nuevo León"
                  required
                  className="input-industrial"
                  data-testid="park-ubicacion-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Moneda</Label>
                <select
                  value={parkForm.moneda}
                  onChange={(e) => setParkForm({ ...parkForm, moneda: e.target.value })}
                  className="input-industrial w-full h-10 px-3"
                  data-testid="park-moneda-select"
                >
                  <option value="MXN">MXN - Peso Mexicano</option>
                  <option value="USD">USD - Dólar Estadounidense</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowParkModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" data-testid="save-park-btn">
                <Save size={16} className="mr-2" />
                {editingPark ? 'Actualizar' : 'Crear Parque'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">
              {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCategory}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="label-overline">Nombre</Label>
                <Input
                  value={categoryForm.nombre}
                  onChange={(e) => setCategoryForm({ ...categoryForm, nombre: e.target.value })}
                  placeholder="Ej: Seguridad, Mantenimiento, Servicios..."
                  required
                  className="input-industrial"
                  data-testid="category-nombre-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Descripción (opcional)</Label>
                <Input
                  value={categoryForm.descripcion}
                  onChange={(e) => setCategoryForm({ ...categoryForm, descripcion: e.target.value })}
                  placeholder="Descripción de la categoría..."
                  className="input-industrial"
                  data-testid="category-descripcion-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCategoryModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" data-testid="save-category-btn">
                <Save size={16} className="mr-2" />
                {editingCategory ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Subcategory Modal */}
      <Dialog open={showSubcategoryModal} onOpenChange={setShowSubcategoryModal}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">
              {editingSubcategory ? 'Editar Subcategoría' : 'Nueva Subcategoría'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveSubcategory}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="label-overline">Nombre</Label>
                <Input
                  value={subcategoryForm.nombre}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, nombre: e.target.value })}
                  placeholder="Ej: Vigilancia, Cámaras, Energía eléctrica..."
                  required
                  className="input-industrial"
                  data-testid="subcategory-nombre-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Descripción (opcional)</Label>
                <Input
                  value={subcategoryForm.descripcion}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, descripcion: e.target.value })}
                  placeholder="Descripción de la subcategoría..."
                  className="input-industrial"
                  data-testid="subcategory-descripcion-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSubcategoryModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" data-testid="save-subcategory-btn">
                <Save size={16} className="mr-2" />
                {editingSubcategory ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ ...deleteConfirm, open })}
        onConfirm={() => {
          if (deleteConfirm.type === 'park') {
            handleDeletePark(deleteConfirm.id);
          } else if (deleteConfirm.type === 'category') {
            handleDeleteCategory(deleteConfirm.id);
          } else if (deleteConfirm.type === 'subcategory') {
            handleDeleteSubcategory(deleteConfirm.id);
          }
        }}
        title="¿Estás seguro que deseas eliminar?"
        description={
          deleteConfirm.type === 'park' 
            ? `El parque "${deleteConfirm.name}" y todos sus datos (lotes, presupuestos, cuotas, gastos) serán eliminados permanentemente.`
            : deleteConfirm.type === 'category'
            ? `La categoría "${deleteConfirm.name}" y todas sus subcategorías serán eliminadas.`
            : `La subcategoría "${deleteConfirm.name}" será eliminada.`
        }
        confirmText="Eliminar"
      />
    </div>
  );
};

export default Configuraciones;
